import { Check, Pencil, X } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/NewButton';
import { MAX_DRAFT_TITLE_LENGTH } from '@/drafting/constants';
import { apiErrorMessage } from '@/lib/api-client';

const TITLE_INPUT_MIN_WIDTH = 48;
const TITLE_INPUT_CHROME_WIDTH = 18;

export function DraftTitleEditor({
  disabled = false,
  onSave,
  title,
}: {
  disabled?: boolean;
  onSave: (title: string) => Promise<void>;
  title: string;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState(title);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [initialEditWidth, setInitialEditWidth] = React.useState<number | null>(null);
  const [measuredDraftWidth, setMeasuredDraftWidth] = React.useState(TITLE_INPUT_MIN_WIDTH);
  const [maxInputWidth, setMaxInputWidth] = React.useState<number | null>(null);
  const editorRef = React.useRef<HTMLSpanElement>(null);
  const titleTextRef = React.useRef<HTMLSpanElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const saveButtonRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const cancelButtonRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  React.useEffect(() => {
    if (!isEditing) {
      setDraftTitle(title);
      setInitialEditWidth(null);
      setMaxInputWidth(null);
    }
  }, [isEditing, title]);

  const updateMaxInputWidth = React.useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const style = window.getComputedStyle(editor);
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0;
    const saveButtonWidth = saveButtonRef.current?.getBoundingClientRect().width ?? 0;
    const cancelButtonWidth = cancelButtonRef.current?.getBoundingClientRect().width ?? 0;
    const reservedWidth = saveButtonWidth + cancelButtonWidth + (gap * 2);
    const availableWidth = Math.floor(editor.getBoundingClientRect().width - reservedWidth);

    setMaxInputWidth(Math.max(TITLE_INPUT_MIN_WIDTH, availableWidth));
  }, []);

  React.useLayoutEffect(() => {
    if (!isEditing) return;
    const width = measureRef.current?.getBoundingClientRect().width;
    if (typeof width === 'number') {
      setMeasuredDraftWidth(Math.ceil(width));
    }
  });

  React.useLayoutEffect(() => {
    if (!isEditing) return undefined;
    updateMaxInputWidth();

    const editor = editorRef.current;
    if (!editor || typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = new ResizeObserver(updateMaxInputWidth);
    resizeObserver.observe(editor);
    return () => resizeObserver.disconnect();
  }, [isEditing, updateMaxInputWidth]);

  React.useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const beginEditing = React.useCallback(() => {
    const titleWidth = titleTextRef.current?.getBoundingClientRect().width ?? 0;
    setInitialEditWidth(Math.ceil(titleWidth + TITLE_INPUT_CHROME_WIDTH));
    setDraftTitle(title);
    setIsEditing(true);
  }, [title]);

  const handleCancel = React.useCallback(() => {
    setDraftTitle(title);
    setIsEditing(false);
  }, [title]);

  const handleSave = React.useCallback(async () => {
    const nextTitle = draftTitle.trim();
    if (!nextTitle) {
      toast.error('Draft name cannot be empty');
      return;
    }
    if (nextTitle.length > MAX_DRAFT_TITLE_LENGTH) {
      toast.error(`Draft name cannot exceed ${MAX_DRAFT_TITLE_LENGTH} characters`);
      return;
    }
    if (nextTitle === title) {
      setIsEditing(false);
      return;
    }

    setIsRenaming(true);
    try {
      await onSave(nextTitle);
      setIsEditing(false);
      toast.success('Draft renamed');
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to rename draft');
    } finally {
      setIsRenaming(false);
    }
  }, [draftTitle, onSave, title]);

  if (!isEditing) {
    return (
      <span className="group/title inline-flex min-w-0 max-w-full items-center gap-2 align-middle">
        <span ref={titleTextRef} className="min-w-0 truncate">{title}</span>
        <Button
          variant="bare"
          size="sm"
          icon={Pencil}
          tooltip="Rename draft"
          aria-label="Rename draft"
          className="text-slate-400 opacity-0 transition-opacity hover:text-slate-700 group-hover/title:opacity-100 focus-visible:opacity-100"
          disabled={disabled}
          onClick={beginEditing}
        />
      </span>
    );
  }

  const inputWidth = Math.max(
    TITLE_INPUT_MIN_WIDTH,
    initialEditWidth ?? 0,
    measuredDraftWidth,
  );
  const cappedInputWidth = maxInputWidth === null
    ? inputWidth
    : Math.min(inputWidth, maxInputWidth);

  return (
    <span ref={editorRef} className="relative flex w-full min-w-0 max-w-full items-center gap-2 align-middle">
      <span
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 whitespace-pre rounded-md border border-transparent px-2 py-1 text-inherit font-[inherit] leading-tight opacity-0"
      >
        {draftTitle || ' '}
      </span>
      <input
        ref={inputRef}
        value={draftTitle}
        aria-label="Draft name"
        className="min-w-0 shrink grow-0 rounded-md border border-slate-300 bg-white px-2 py-1 text-inherit font-[inherit] leading-tight text-slate-900 shadow-xs outline-hidden focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
        disabled={isRenaming}
        maxLength={MAX_DRAFT_TITLE_LENGTH}
        style={{ width: cappedInputWidth }}
        onChange={(event) => setDraftTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void handleSave();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            handleCancel();
          }
        }}
      />
      <Button
        ref={saveButtonRef}
        variant="bare"
        size="sm"
        icon={Check}
        tooltip="Save name"
        aria-label="Save draft name"
        className="text-teal-700 hover:text-teal-800"
        disabled={isRenaming}
        onClick={() => {
          void handleSave();
        }}
      />
      <Button
        ref={cancelButtonRef}
        variant="bare"
        size="sm"
        icon={X}
        tooltip="Cancel rename"
        aria-label="Cancel rename"
        className="text-slate-400 hover:text-slate-700"
        disabled={isRenaming}
        onClick={handleCancel}
      />
    </span>
  );
}
